<template>
  <div class="flex flex-row items-center justify-between mb-1 flex-wrap gap-2">
    <span class="font-bold text-black">Registro de plantão</span>
    <div
      v-if="!isReadOnly"
      @click="addNewCheck"
      class="flex items-center justify-center cursor-pointer border rounded-md py-1 px-3"
      :class="borderColor"
    >
      <button :disabled="!toggleEdit" class="flex gap-2 md:gap-4 items-center">
        <span class="font-bold text-sm hidden md:inline" :class="colorText"
          >Adicionar registro de presença</span
        >
        <span class="font-bold text-sm md:hidden" :class="colorText">Adicionar</span>
        <rounded-plus :color="colorHex" />
      </button>
    </div>
  </div>

  <!-- Aviso literal de M1 (Tela 2). O texto vem de uma constante, e não do
       template, para que a frase chegue inteira ao DOM (o template quebraria a
       linha e o usuário — e o teste — leriam a frase picotada por espaços). -->
  <div
    v-if="isReadOnly"
    class="flex items-start gap-2 rounded-lg border border-primary bg-primary5 p-3 mb-2 text-sm text-left"
    data-testid="doctor-checks-readonly-banner"
  >
    <v-icon :icon="mdiLockOutline" color="primary" size="20" class="shrink-0 mt-[2px]" />
    <span><b>Somente leitura.</b> {{ MANUAL_CHECK_BLOCK_NOTICE }}</span>
  </div>

  <div class="flex flex-col w-full rounded-lg p-2 border-[2px] border-primary mb-4">
    <div v-if="!isReadOnly" class="cursor-pointer my-2 flex items-center gap-1 justify-between">
      <edit-pencil-icon v-if="!toggleEdit" @click="openEditMode" color="#2F7F91" />
      <div v-if="toggleEdit" class="flex gap-2 text-xs text-gray-500 font-bold">
        <span @click="saveEditMode">
          <v-icon :icon="mdiCheckCircleOutline" color="#22bb33" class="cursor-pointer" />
          Salvar Rascunho
        </span>
        <span @click="closeEditMode">
          <v-icon :icon="mdiCloseCircleOutline" color="#bb2124" class="cursor-pointer" />
          Descartar Rascunho
        </span>
      </div>
    </div>

    <div v-if="!isReadOnly" class="hidden md:flex w-full">
      <div class="w-[10%]"></div>

      <div class="w-[20%] text-center">
        <h1>Checkin</h1>
      </div>

      <div class="w-[25%] text-center">
        <h1>Checkout</h1>
      </div>

      <div class="w-[15%] text-center">
        <h1>Registro</h1>
      </div>

      <div class="w-[25%] text-center">
        <h1>Evidência</h1>
      </div>

      <div class="w-[5%]"></div>
    </div>

    <!-- Colunas de M1 (Tela 2). "Anexos" deixa de ser exclusiva da origem WEB:
         é o que faz o anexo do Registro de Atividade aparecer aqui sem código
         novo, e o que permite ver os anexos de qualquer fonte. -->
    <div v-else class="hidden md:flex w-full text-sm font-bold text-gray-500">
      <div class="w-[8%] text-center">Principal</div>
      <div class="w-[16%] text-center">Check-in</div>
      <div class="w-[16%] text-center">Check-out</div>
      <div class="w-[24%] text-center">Registro (fonte)</div>
      <div class="w-[16%] text-center">Anexos</div>
      <div class="w-[20%] text-center">Ações</div>
    </div>

    <div v-if="!isReadOnly">
      <div v-for="(info, i) in draftInformations" :key="i">
        <!-- Desktop row -->
        <div class="hidden md:flex w-full items-center mt-3">
          <div
            class="w-[10%] h-[44px] flex items-center justify-center text-xs font-bold text-gray-500"
          >
            <!-- Indicador, não controle (BF5): quem decide o check principal é o
                 `ensureMainCheck` do servidor — o rádio daqui nunca teve efeito
                 confiável e virou leitura, igual ao modo somente leitura abaixo. -->
            <button
              type="button"
              disabled
              class="text-primary cursor-default"
              :data-testid="`doctor-checks-main-indicator-${i}`"
              :aria-label="isRowMain(i) ? 'Registro principal' : 'Registro não principal'"
            >
              {{ isRowMain(i) ? '●' : '○' }}
            </button>
          </div>
          <div class="w-[20%] h-[40px] flex items-start">
            <div class="h-full mr-1">
              <a
                v-if="
                  info.origin === 'DOCTOR' &&
                  info.checkinLatitude &&
                  info.checkinLongitude &&
                  healthUnitLatitude &&
                  healthUnitLongitude
                "
                class="h-full flex justify-center items-center"
                :href="`https://www.google.com/maps/dir/${info.checkinLatitude},${info.checkinLongitude}/${healthUnitLatitude},${healthUnitLongitude}`"
                target="_blank"
              >
                <v-icon class="hover:cursor-pointer" :icon="mdiMapMarkerRadius" color="primary" />
              </a>
              <div v-else class="w-[24px]"></div>
            </div>
            <GenericInput
              :disabled="!toggleEdit || info.origin === 'DOCTOR'"
              v-model="info.checkin"
              name="checkin"
              density="compact"
              :label="info.labelCheckin || ''"
              v-maska="'##:##'"
              width="300px"
              variant="outlined"
            />
            <LabelBadge
              v-if="flags.enabledPermanenceRecord && info.permanence?.autoCheckin"
              label="🔗 PERMANÊNCIA"
              color="blue"
              size="small"
              :icon="null"
              class="ml-1 shrink-0 self-center"
              data-testid="permanence-checkin-chip"
            />
          </div>
          <div class="w-[25%] h-[40px] flex items-start">
            <div class="h-full mr-1">
              <a
                v-if="
                  info.origin === 'DOCTOR' &&
                  info.checkoutLatitude &&
                  info.checkoutLongitude &&
                  healthUnitLatitude &&
                  healthUnitLongitude
                "
                class="h-full flex justify-center items-center"
                :href="`https://www.google.com/maps/dir/${info.checkoutLatitude},${info.checkoutLongitude}/${healthUnitLatitude},${healthUnitLongitude}`"
                target="_blank"
              >
                <v-icon class="hover:cursor-pointer" :icon="mdiMapMarkerRadius" color="primary" />
              </a>
              <div v-else class="w-[24px]"></div>
            </div>
            <GenericInput
              :disabled="!toggleEdit || info.origin === 'DOCTOR'"
              v-model="info.checkout"
              name="checkout"
              density="compact"
              :label="info.labelCheckout || ''"
              v-maska="'##:##'"
              width="100%"
              @click:prepend="
                (e) => {
                  console.log('prepend', e)
                }
              "
              variant="outlined"
            />
            <LabelBadge
              v-if="flags.enabledPermanenceRecord && info.permanence?.autoCheckout"
              label="🔗 PERMANÊNCIA"
              color="blue"
              size="small"
              :icon="null"
              class="ml-1 shrink-0 self-center"
              data-testid="permanence-checkout-chip"
            />
          </div>
          <div
            class="w-[15%] h-[44px] flex items-center justify-center text-xs font-bold text-gray-500 whitespace-nowrap"
          >
            {{ getCheckOriginLabel(info.origin) }}
          </div>

          <template v-if="info.origin === 'WEB'">
            <div class="w-[25%] h-auto flex flex-col items-center">
              <div v-if="toggleEdit">
                <div
                  v-if="
                    (!draftInformations[i]?.evidence ||
                      draftInformations[i]?.evidence?.length === 0) &&
                    (!draftInformations[i]?.checkEvidences ||
                      draftInformations[i]?.checkEvidences?.length === 0)
                  "
                  class="w-full"
                >
                  <v-btn
                    color="primary"
                    depressed
                    class="text-xs w-full md:w-[200px]"
                    @click="openModal(i)"
                  >
                    Selecionar Arquivos
                  </v-btn>
                </div>
                <div v-else class="w-full">
                  <v-btn
                    color="primary"
                    depressed
                    class="text-xs w-full md:w-[200px]"
                    @click="showFiles(selectedFileNames[i], i)"
                  >
                    {{ selectedFileNames[i].length }} Arquivos
                  </v-btn>
                </div>
              </div>
              <div v-else>
                <div
                  v-if="
                    (draftInformations[i]?.checkEvidences?.length ?? 0) > 0 ||
                    (draftInformations[i]?.evidence?.length ?? 0) > 0
                  "
                  class="w-full"
                >
                  <div class="w-full">
                    <v-btn
                      color="primary"
                      depressed
                      class="text-xs w-full md:w-[200px]"
                      @click="showFiles(selectedFileNames[i], i)"
                    >
                      {{ selectedFileNames[i].length }} Arquivos
                    </v-btn>
                  </div>
                </div>
                <div v-else class="w-full">
                  <span class="block text-xs text-gray-500 text-center">--</span>
                </div>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="w-[25%] flex flex-col items-center">
              <span class="block text-xs text-gray-500 text-center">--</span>
            </div>
          </template>

          <button
            :disabled="!toggleEdit || info.origin === 'DOCTOR'"
            @click="removeCheck(i)"
            class="w-[5%] h-[44px] flex items-center"
            data-testid="doctor-checks-remove-row"
          >
            <v-icon
              :icon="mdiTrashCanOutline"
              :color="info.origin === 'DOCTOR' ? 'error' : colorHex"
            />
          </button>
        </div>

        <!-- Mobile card -->
        <div class="flex md:hidden flex-col gap-2 mt-3 p-3 border rounded-lg bg-gray-50">
          <div class="flex items-center gap-2">
            <button
              type="button"
              disabled
              class="text-primary cursor-default"
              :data-testid="`doctor-checks-main-indicator-mobile-${i}`"
              :aria-label="isRowMain(i) ? 'Registro principal' : 'Registro não principal'"
            >
              {{ isRowMain(i) ? '●' : '○' }}
            </button>
            <span class="text-xs font-bold text-gray-500">
              {{ getCheckOriginLabel(info.origin) }}
            </span>
          </div>

          <div class="flex flex-col gap-1">
            <label class="text-xs text-gray-500 font-bold">Check-in</label>
            <div class="flex items-center gap-1">
              <a
                v-if="
                  info.origin === 'DOCTOR' &&
                  info.checkinLatitude &&
                  info.checkinLongitude &&
                  healthUnitLatitude &&
                  healthUnitLongitude
                "
                class="flex justify-center items-center"
                :href="`https://www.google.com/maps/dir/${info.checkinLatitude},${info.checkinLongitude}/${healthUnitLatitude},${healthUnitLongitude}`"
                target="_blank"
              >
                <v-icon class="hover:cursor-pointer" :icon="mdiMapMarkerRadius" color="primary" />
              </a>
              <GenericInput
                :disabled="!toggleEdit || info.origin === 'DOCTOR'"
                v-model="info.checkin"
                name="checkin"
                density="compact"
                :label="info.labelCheckin || ''"
                v-maska="'##:##'"
                width="100%"
                variant="outlined"
              />
              <LabelBadge
                v-if="flags.enabledPermanenceRecord && info.permanence?.autoCheckin"
                label="🔗 PERMANÊNCIA"
                color="blue"
                size="small"
                :icon="null"
                data-testid="permanence-checkin-chip"
              />
            </div>
          </div>

          <div class="flex flex-col gap-1">
            <label class="text-xs text-gray-500 font-bold">Check-out</label>
            <div class="flex items-center gap-1">
              <a
                v-if="
                  info.origin === 'DOCTOR' &&
                  info.checkoutLatitude &&
                  info.checkoutLongitude &&
                  healthUnitLatitude &&
                  healthUnitLongitude
                "
                class="flex justify-center items-center"
                :href="`https://www.google.com/maps/dir/${info.checkoutLatitude},${info.checkoutLongitude}/${healthUnitLatitude},${healthUnitLongitude}`"
                target="_blank"
              >
                <v-icon class="hover:cursor-pointer" :icon="mdiMapMarkerRadius" color="primary" />
              </a>
              <GenericInput
                :disabled="!toggleEdit || info.origin === 'DOCTOR'"
                v-model="info.checkout"
                name="checkout"
                density="compact"
                :label="info.labelCheckout || ''"
                v-maska="'##:##'"
                width="100%"
                variant="outlined"
              />
              <LabelBadge
                v-if="flags.enabledPermanenceRecord && info.permanence?.autoCheckout"
                label="🔗 PERMANÊNCIA"
                color="blue"
                size="small"
                :icon="null"
                data-testid="permanence-checkout-chip"
              />
            </div>
          </div>

          <div class="flex items-center justify-between">
            <template v-if="info.origin === 'WEB'">
              <div class="flex-1">
                <div v-if="toggleEdit">
                  <div
                    v-if="
                      (!draftInformations[i]?.evidence ||
                        draftInformations[i]?.evidence?.length === 0) &&
                      (!draftInformations[i]?.checkEvidences ||
                        draftInformations[i]?.checkEvidences?.length === 0)
                    "
                  >
                    <v-btn color="primary" depressed class="text-xs w-full" @click="openModal(i)">
                      Selecionar Arquivos
                    </v-btn>
                  </div>
                  <div v-else>
                    <v-btn
                      color="primary"
                      depressed
                      class="text-xs w-full"
                      @click="showFiles(selectedFileNames[i], i)"
                    >
                      {{ selectedFileNames[i].length }} Arquivos
                    </v-btn>
                  </div>
                </div>
                <div v-else>
                  <div
                    v-if="
                      (draftInformations[i]?.checkEvidences?.length ?? 0) > 0 ||
                      (draftInformations[i]?.evidence?.length ?? 0) > 0
                    "
                  >
                    <v-btn
                      color="primary"
                      depressed
                      class="text-xs w-full"
                      @click="showFiles(selectedFileNames[i], i)"
                    >
                      {{ selectedFileNames[i].length }} Arquivos
                    </v-btn>
                  </div>
                  <div v-else>
                    <span class="block text-xs text-gray-500 text-left">--</span>
                  </div>
                </div>
              </div>
            </template>
            <template v-else>
              <span class="block text-xs text-gray-500">--</span>
            </template>

            <button
              :disabled="!toggleEdit || info.origin === 'DOCTOR'"
              @click="removeCheck(i)"
              class="ml-2 h-[44px] flex items-center"
              data-testid="doctor-checks-remove-row"
            >
              <v-icon
                :icon="mdiTrashCanOutline"
                :color="info.origin === 'DOCTOR' ? 'error' : colorHex"
              />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modo somente leitura (M1, Tela 2 / §4.4). É um bloco separado, e não um
         punhado de `:disabled` no bloco de edição, por dois motivos: o caminho de
         rollback (flag desligada) continua byte a byte o de hoje, e o que se lê
         aqui não é mais "as linhas que eu digito" e sim TODAS as fontes que o
         plantão tem — inclusive as que perderam o ranking. -->
    <div v-else data-testid="doctor-checks-readonly-table">
      <div v-for="(info, i) in draftInformations" :key="`readonly-${i}`">
        <!-- Desktop row -->
        <div
          class="hidden md:flex w-full items-center mt-3 pb-2 border-b border-gray-100 text-sm"
          :data-testid="`doctor-checks-readonly-row-${i}`"
        >
          <div class="w-[8%] flex justify-center">
            <!-- Indicador, não controle: quem decide o check principal é o
                 ranking de precedência das fontes, não um clique nesta tela. -->
            <button
              type="button"
              disabled
              class="text-primary cursor-default"
              data-testid="doctor-checks-main-radio"
              :aria-label="isRowMain(i) ? 'Registro principal' : 'Registro não principal'"
            >
              {{ isRowMain(i) ? '●' : '○' }}
            </button>
          </div>

          <div class="w-[16%] flex flex-col items-center">
            <span class="font-mono">{{ info.checkin || '—' }}</span>
            <span v-if="info.labelCheckin" class="text-xs text-gray-500">
              {{ info.labelCheckin }}
            </span>
            <LabelBadge
              v-if="flags.enabledPermanenceRecord && info.permanence?.autoCheckin"
              label="🔗 PERMANÊNCIA"
              color="blue"
              size="small"
              :icon="null"
              class="mt-1"
              data-testid="permanence-checkin-chip"
            />
          </div>

          <div class="w-[16%] flex flex-col items-center">
            <span class="font-mono">{{ info.checkout || '—' }}</span>
            <span v-if="info.labelCheckout" class="text-xs text-gray-500">
              {{ info.labelCheckout }}
            </span>
            <LabelBadge
              v-if="flags.enabledPermanenceRecord && info.permanence?.autoCheckout"
              label="🔗 PERMANÊNCIA"
              color="blue"
              size="small"
              :icon="null"
              class="mt-1"
              data-testid="permanence-checkout-chip"
            />
          </div>

          <div class="w-[24%] flex flex-col items-center text-center gap-1">
            <span>{{ getCheckOriginLabel(info.origin) }}</span>
            <LabelBadge
              v-if="!isRowMain(i)"
              label="descartada"
              color="gray"
              size="small"
              :icon="null"
              :data-testid="`doctor-checks-superseded-${i}`"
            />
          </div>

          <div class="w-[16%] flex justify-center">
            <button
              v-if="attachmentCount(info) > 0"
              type="button"
              class="text-primary underline"
              :data-testid="`doctor-checks-attachments-${i}`"
              @click="showFiles(selectedFileNames[i], i)"
            >
              {{ attachmentLabel(info) }}
            </button>
            <span v-else class="text-gray-500" :data-testid="`doctor-checks-attachments-${i}`">
              —
            </span>
          </div>

          <div class="w-[20%] flex flex-col items-center gap-1">
            <a
              v-if="mapsUrl(info)"
              class="text-primary underline"
              :href="mapsUrl(info) || undefined"
              target="_blank"
              rel="noopener"
              :data-testid="`doctor-checks-map-link-${i}`"
            >
              Ver mapa
            </a>
            <button
              type="button"
              class="text-primary underline"
              :data-testid="`doctor-checks-source-toggle-${i}`"
              @click="toggleSourceDetails(i)"
            >
              ver origem
            </button>
            <a
              v-if="containerUrl(info)"
              class="text-primary underline"
              :href="containerUrl(info) || undefined"
              target="_blank"
              rel="noopener"
              :data-testid="`doctor-checks-container-link-${i}`"
            >
              ver registro #{{ info.dsAttendanceContainerId }}
            </a>
          </div>
        </div>

        <!-- Mobile card -->
        <div class="flex md:hidden flex-col gap-2 mt-3 p-3 border rounded-lg bg-gray-50 text-sm">
          <div class="flex items-center justify-between gap-2">
            <span class="font-bold text-gray-500">{{ getCheckOriginLabel(info.origin) }}</span>
            <span class="text-primary">{{ isRowMain(i) ? '● Principal' : '○' }}</span>
          </div>
          <LabelBadge
            v-if="!isRowMain(i)"
            label="descartada"
            color="gray"
            size="small"
            :icon="null"
          />
          <div class="flex flex-col gap-1">
            <span><b>Check-in:</b> {{ info.checkin || '—' }}</span>
            <span><b>Check-out:</b> {{ info.checkout || '—' }}</span>
            <span><b>Anexos:</b> {{ attachmentLabel(info) }}</span>
          </div>
          <div class="flex items-center gap-3 flex-wrap">
            <a
              v-if="mapsUrl(info)"
              class="text-primary underline"
              :href="mapsUrl(info) || undefined"
              target="_blank"
              rel="noopener"
            >
              Ver mapa
            </a>
            <button type="button" class="text-primary underline" @click="toggleSourceDetails(i)">
              ver origem
            </button>
            <a
              v-if="containerUrl(info)"
              class="text-primary underline"
              :href="containerUrl(info) || undefined"
              target="_blank"
              rel="noopener"
            >
              ver registro #{{ info.dsAttendanceContainerId }}
            </a>
            <button
              v-if="attachmentCount(info) > 0"
              type="button"
              class="text-primary underline"
              @click="showFiles(selectedFileNames[i], i)"
            >
              {{ attachmentLabel(info) }}
            </button>
          </div>
        </div>

        <!-- "ver origem": o detalhe abre aqui mesmo. Abrir o painel de fontes da
             Tela 1 exigiria trocar de modal e perder a edição em andamento. -->
        <div
          v-if="openedSourceDetails.has(i)"
          class="mt-1 mb-2 p-3 rounded-lg bg-gray-50 text-xs text-left"
          :data-testid="`doctor-checks-source-details-${i}`"
        >
          <div><b>Fonte:</b> {{ getCheckOriginLabel(info.origin) }}</div>
          <div>
            <b>Posição:</b>
            {{ isRowMain(i) ? 'fonte vigente do horário efetivo' : 'superada no ranking' }}
          </div>
          <div v-if="info.permanence?.autoCheckin || info.permanence?.autoCheckout">
            <b>Método:</b> permanência automática
          </div>
          <div v-if="info.checkinLatitude && info.checkinLongitude">
            <b>Coordenadas do check-in:</b> {{ info.checkinLatitude }}, {{ info.checkinLongitude }}
          </div>
          <div v-if="info.checkoutLatitude && info.checkoutLongitude">
            <b>Coordenadas do check-out:</b>
            {{ info.checkoutLatitude }}, {{ info.checkoutLongitude }}
          </div>
        </div>
      </div>
    </div>

    <PermanenceLinkRow
      v-for="link in permanenceLinks"
      :key="`${link.direction}-${link.recordId}`"
      :direction="link.direction"
      :station-label="link.stationLabel"
      :doctor-station-id="link.doctorStationId"
      :station-id="link.stationId"
      @navigate="(stationId) => emit('navigateToLinkedStation', stationId)"
    />
  </div>

  <EvidenceFlowDisabledModal v-model="flowDisabledOpen" />

  <v-dialog v-if="!isReadOnly" v-model="showModal" max-width="700">
    <v-card>
      <v-card-title color="primary">Selecione os arquivos</v-card-title>

      <v-card-text>
        <div
          v-if="!flags.disabledManualEvidenceUpload"
          color="primary"
          class="custom-dropzone"
          data-testid="doctor-checks-dropzone"
          @drop.prevent="onDrop"
          @dragover.prevent
        >
          <v-icon size="64" color="primary" :icon="mdiTrayArrowUp"></v-icon>

          <div class="mt-2 text-center">
            Arraste os arquivos ou
            <a class="text-bold text-primary" href="#" @click.prevent="triggerFileInput"
              >clique aqui</a
            >
            para selecionar
          </div>
        </div>

        <input type="file" ref="fileInput" @change="onFileSelect" multiple hidden />

        <div v-if="modalFiles.length > 0">
          <v-sheet
            v-for="(item, index) in modalFiles"
            :key="item.id"
            class="file-list-item"
            variant="outlined"
            rounded
          >
            <v-row class="file-list-item-row" no-gutters>
              <v-col cols="auto">
                <v-icon size="40" color="primary" class="ml-0" :icon="mdiFileOutline" />
              </v-col>

              <v-col class="col-texto-flexivel">
                <v-text-field
                  v-if="item.isEditing"
                  v-model="item.newName"
                  density="compact"
                  hide-details
                  autofocus
                  @keydown.enter="item.isEditing = false"
                ></v-text-field>

                <div v-else style="max-width: 200px">
                  <div class="font-bold truncate">
                    {{ item.name }}
                  </div>
                  <div v-if="item.size" class="text-sm text-gray-500">
                    {{ formatBytes(item.size) }}
                  </div>
                </div>
              </v-col>

              <v-col cols="auto">
                <div class="d-flex align-center pa-2">
                  <v-autocomplete
                    v-model="item.eventType"
                    :items="['Check-in', 'Check-out', 'Check-in e Check-out']"
                    label="Categoria"
                    style="width: 100%; max-width: 250px"
                    variant="outlined"
                    density="comfortable"
                    color="primary20"
                    base-color="primary20"
                    :auto-select-first="true"
                    hide-details
                    @update:model-value="hasBackendChanges = true"
                  >
                  </v-autocomplete>
                </div>
              </v-col>

              <v-col cols="auto" v-if="item.id">
                <v-btn
                  icon
                  variant="text"
                  color="primary"
                  @click.stop="downloadEvidence(item.id)"
                  density="compact"
                >
                  <v-icon size="24" :icon="mdiTrayArrowDown" />
                </v-btn>
              </v-col>

              <v-col cols="auto">
                <edit-pencil-icon
                  v-if="!item.isEditing"
                  color="#2F7F91"
                  class="cursor-pointer"
                  @click="item.isEditing = true"
                />

                <div v-else class="d-flex align-center">
                  <v-icon
                    :icon="mdiCheckBold"
                    color="primary"
                    class="cursor-pointer"
                    @click="saveCheckEvidenceName(item)"
                  />
                  <v-icon
                    :icon="mdiClose"
                    color="primary"
                    class="cursor-pointer ml-1"
                    @click="cancelEditCheckEvidenceName(item)"
                  />
                </div>
              </v-col>
              <v-col cols="auto">
                <v-icon
                  :icon="mdiCloseCircleOutline"
                  color="#2F7F91"
                  class="cursor-pointer"
                  @click="removeBackendFile(index)"
                />
              </v-col>
            </v-row>
          </v-sheet>
        </div>

        <div v-if="filesToUpload.length > 0">
          <v-sheet
            v-for="(item, index) in filesToUpload"
            :key="item.id"
            class="file-list-item"
            variant="outlined"
            rounded
          >
            <v-row class="file-list-item-row" no-gutters>
              <v-col cols="auto">
                <v-icon size="40" color="primary" class="ml-0" :icon="mdiFileOutline" />
              </v-col>

              <v-col class="col-texto-flexivel">
                <v-text-field
                  v-if="item.isEditing"
                  v-model="item.newName"
                  density="compact"
                  hide-details
                  autofocus
                  @keydown.enter="item.isEditing = false"
                ></v-text-field>

                <div v-else style="max-width: 200px">
                  <div class="font-bold truncate">
                    {{ item.newName }}
                  </div>
                  <div class="text-sm text-gray-500">
                    {{ formatBytes(item.file.size) }}
                  </div>
                </div>
              </v-col>

              <v-col cols="auto">
                <div class="d-flex align-center pa-2">
                  <v-autocomplete
                    v-model="item.eventType"
                    :items="['Check-in', 'Check-out', 'Check-in e Check-out']"
                    label="Categoria"
                    style="width: 100%; max-width: 250px"
                    variant="outlined"
                    density="comfortable"
                    color="primary20"
                    base-color="primary20"
                    :auto-select-first="true"
                    hide-details
                  >
                  </v-autocomplete>
                </div>
              </v-col>

              <v-col cols="auto">
                <v-btn
                  icon
                  variant="text"
                  color="primary"
                  @click.stop="downloadLocalFile(item.file)"
                  density="compact"
                >
                  <v-icon size="24" :icon="mdiTrayArrowDown" />
                </v-btn>
              </v-col>

              <v-col cols="auto">
                <edit-pencil-icon
                  v-if="!item.isEditing"
                  color="#2F7F91"
                  class="cursor-pointer"
                  @click="item.isEditing = true"
                />

                <div v-else class="d-flex align-center">
                  <v-icon
                    :icon="mdiCheckBold"
                    color="primary"
                    class="cursor-pointer"
                    @click="item.isEditing = false"
                  />
                  <v-icon
                    :icon="mdiClose"
                    color="primary"
                    class="cursor-pointer ml-1"
                    @click="cancelEditName(item)"
                  />
                </div>
              </v-col>
              <v-col cols="auto">
                <v-icon
                  :icon="mdiCloseCircleOutline"
                  color="#2F7F91"
                  class="cursor-pointer"
                  @click="removeFile(index)"
                />
              </v-col>
            </v-row>
          </v-sheet>
        </div>
      </v-card-text>

      <v-card-actions
        style="display: flex; width: 100%; justify-content: space-between; padding: 1.5rem"
      >
        <OutlinedButton @click="closeFilesModal" text="Voltar" height="48px" width="74px" />

        <filled-button
          :disabled="!isFormValid"
          @click="submitFiles"
          text="Salvar"
          variant="flat"
          color="primary"
          size="large"
          height="48px"
          width="180px"
          class="!bg-[#337C88]"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="showFilesModal" max-width="700">
    <v-card>
      <v-card-title class="text-h6 pa-4" style="position: relative">
        {{ isReadOnly ? 'Anexos' : 'Evidências' }}
        <v-btn
          icon
          variant="text"
          @click="closeFilesModal"
          style="position: absolute; right: 8px; top: 8px"
        >
          <v-icon :icon="mdiClose" size="24"></v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text style="padding: 16px !important">
        <div v-if="modalFiles && modalFiles.length > 0">
          <v-row
            v-for="(file, idx) in modalFiles"
            :key="idx"
            class="ma-0 border-b pl-4 pr-2 flex-nowrap"
            style="height: 72px"
            no-gutters
          >
            <v-col cols="auto" style="display: flex; align-items: center; margin-right: 20px">
              <v-icon size="40" color="primary" :icon="mdiFileOutline" />
            </v-col>

            <v-col
              style="
                display: flex;
                flex-direction: column;
                justify-content: center;
                height: 100%;
                min-width: 0;
                flex-grow: 1;
              "
            >
              <div
                class="font-weight-bold text-truncate"
                :title="file.name"
                style="line-height: 1.2"
              >
                {{ file.name }}
              </div>

              <div
                v-if="file.size"
                class="text-caption text-grey-darken-1 text-truncate mt-1"
                style="line-height: 1"
              >
                {{ formatBytes(file.size) }}
              </div>
            </v-col>

            <v-col cols="auto" style="display: flex; align-items: center; padding-left: 12px">
              <span
                v-if="file.eventType"
                class="text-body-2 text-grey-darken-2 me-3 text-no-wrap"
                style="line-height: 1; padding-top: 2px"
              >
                {{ eventTypeLabel(file.eventType) }}
              </span>

              <v-btn
                v-if="file.id"
                icon
                variant="text"
                color="primary"
                @click.stop="downloadEvidence(file.id)"
                style="display: flex; align-items: center; justify-content: center"
              >
                <v-icon size="32" :icon="mdiTrayArrowDown"></v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </v-card-text>

      <v-card-actions style="width: 100%; display: flex; justify-content: flex-end; padding: 16px">
        <OutlinedButton
          @click="closeFilesModal"
          text="Fechar"
          height="48px"
          width="74px"
          style="border: 2px solid currentColor; font-weight: bold"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { utcToZonedTime } from 'date-fns-tz'
import { format } from 'date-fns'
import GenericInput from '@/components/designSystem/Inputs/GenericInput.vue'
import RoundedPlus from '@/assets/icons/rounded-plus.vue'
import EditPencilIcon from '@/assets/icons/edit-pencil.vue'
import {
  mdiTrashCanOutline,
  mdiCheckCircleOutline,
  mdiCloseCircleOutline,
  mdiMapMarkerRadius,
  mdiTrayArrowUp,
  mdiFileOutline,
  mdiCheckBold,
  mdiClose,
  mdiTrayArrowDown,
  mdiLockOutline
} from '@mdi/js'
import { CheckOrigin, HistoryDate, CheckEvidence } from '@/Network/Types/Requests/StationRequests'
import NetworkService from '@/Network'
import PATHS from '@/router/paths'
import { getCheckOriginLabel } from '@/utils/doctorStation'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import PermanenceLinkRow from '@/components/designSystem/Cards/PermanenceLinkRow.vue'
import EvidenceFlowDisabledModal from '@/modules/company/Dashboard/modals/updateStation/components/EvidenceFlowDisabledModal.vue'
import { getPermanenceLinkRows } from '@/utils/permanence'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useErrorToast } from '@/composables/useErrorToast'

/**
 * Uma linha da tabela. `dsAttendanceContainerId` deixou de ser suposição local: o
 * B17 (§1.2) o fixou em `doctorStation.doctorChecks[]` do `GET /stations/:id`, então
 * o campo mora agora no `HistoryDate` compartilhado. Continua opcional — o link
 * "ver registro #id" só aparece quando o backend diz de qual container a linha nasceu.
 */
type CheckRow = HistoryDate

/**
 * Aviso literal de M1 (Tela 2). Mora aqui, e não no template, porque o Vue
 * quebraria a frase em várias linhas de texto e ela chegaria picotada ao DOM.
 */
const MANUAL_CHECK_BLOCK_NOTICE =
  'O lançamento manual de check-in e check-out foi descontinuado para todos os perfis. ' +
  'Para corrigir um horário, use Enviar registro → Registro de Atividade; correções do ' +
  'backoffice usam o tipo Ajuste administrativo e passam pela mesma auditoria.'

const props = withDefaults(
  defineProps<{
    stationId: number
    infos: HistoryDate[]
    timezone?: string
    healthUnitLatitude?: string
    healthUnitLongitude?: string
    /**
     * Q4/§4.4 — flag GLOBAL servida pelo backend (`registration-settings` ou o
     * campo aditivo de `GET /stations/:id`). Nasce `false`, e `false` é
     * exatamente o comportamento de hoje: é o caminho de rollback de §10.5.
     */
    manualCheckBlockEnabled?: boolean
  }>(),
  { manualCheckBlockEnabled: false }
)

/** Só o `true` explícito bloqueia — backend antigo (campo ausente) não muda nada. */
const isReadOnly = computed(() => props.manualCheckBlockEnabled === true)

const emit = defineEmits([
  'saveChecks',
  'editModeChanged',
  'hasPendingChanges',
  'navigateToLinkedStation'
])

const { flags } = useFeatureFlags()
const { notifyError } = useErrorToast()

const draftInformations = ref<CheckRow[]>([])
const isCheckMain = ref<number | undefined>(undefined)
const toggleEdit = ref<boolean>(false)
const informations = ref<CheckRow[]>([])
/** Linhas com o detalhe de "ver origem" aberto (M1, Tela 2). */
const openedSourceDetails = ref<Set<number>>(new Set())

const isRowMain = (index: number): boolean => index === isCheckMain.value

function attachmentCount(info: CheckRow): number {
  return (info.checkEvidences?.length ?? 0) || (info.evidence?.length ?? 0)
}

function attachmentLabel(info: CheckRow): string {
  const total = attachmentCount(info)
  if (total === 0) return '—'
  return total === 1 ? '1 anexo' : `${total} anexos`
}

/**
 * O mapa deixa de ser exclusivo da origem `DOCTOR`: a regra de M1 é "quando há
 * lat/long", venha a coordenada de onde vier.
 */
function mapsUrl(info: CheckRow): string | null {
  const latitude = info.checkinLatitude ?? info.checkoutLatitude
  const longitude = info.checkinLongitude ?? info.checkoutLongitude
  if (!latitude || !longitude) return null
  if (!props.healthUnitLatitude || !props.healthUnitLongitude) return null
  return `https://www.google.com/maps/dir/${latitude},${longitude}/${props.healthUnitLatitude},${props.healthUnitLongitude}`
}

/**
 * Um check só nasce quando o container é APROVADO (`syncDoctorChecksFromEntries`),
 * então a linha que veio de um container sempre aponta para a rota de aprovado.
 */
function containerUrl(info: CheckRow): string | null {
  const containerId = info.dsAttendanceContainerId
  if (!containerId) return null
  return PATHS.DS_ATTENDANCE_APPROVED.replace(':attendanceId', String(containerId))
}

function toggleSourceDetails(index: number): void {
  const opened = new Set(openedSourceDetails.value)
  if (opened.has(index)) opened.delete(index)
  else opened.add(index)
  openedSourceDetails.value = opened
}
const selectedFileNames = ref<CheckEvidence[][]>([])
const isSavingDraft = ref(false)
const hasBackendChanges = ref(false)

const eventTypeLabel = (v?: string | null) =>
  v === 'checkin'
    ? 'Check-in'
    : v === 'checkout'
      ? 'Check-out'
      : v === 'checkin-checkout'
        ? 'Check-in e Check-out'
        : (v ?? '')

const openEditMode = () => {
  if (isReadOnly.value) return
  toggleEdit.value = !toggleEdit.value
}

const hasChanges = computed(() => {
  if (!toggleEdit.value) return false

  const draft = draftInformations.value
  const original = informations.value

  if (draft.length !== original.length) return true

  // `isMain` saiu da comparação (BF5): não é mais editável nesta tela, então
  // nunca diverge entre rascunho e original por ação do usuário.
  // eslint-disable-next-line complexity
  return draft.some((draftItem, index) => {
    const originalItem = original[index]

    if (draftItem.checkin !== originalItem.checkin) return true
    if (draftItem.checkout !== originalItem.checkout) return true

    const draftCheckEvs = draftItem.checkEvidences || []
    const originalCheckEvs = originalItem.checkEvidences || []

    if (draftCheckEvs.length !== originalCheckEvs.length) return true

    const hasCheckEvDiff = draftCheckEvs.some((ev, evIndex) => {
      const orgEv = originalCheckEvs[evIndex]
      return ev.name !== orgEv.name || ev.eventType !== orgEv.eventType
    })
    if (hasCheckEvDiff) return true

    const draftEvidence = draftItem.evidence || []
    const originalEvidence = originalItem.evidence || []

    if (draftEvidence.length !== originalEvidence.length) return true

    const hasFileDiff = draftEvidence.some((file, fIndex) => {
      const orgFile = originalEvidence[fIndex]
      return (
        file.name !== orgFile.name ||
        file.size !== orgFile.size ||
        file.lastModified !== orgFile.lastModified
      )
    })
    if (hasFileDiff) return true

    return false
  })
})

watch(hasChanges, (newValue) => {
  console.log('hasChanges changed:', newValue)
  emit('hasPendingChanges', newValue)
})

type StagedFile = {
  id: number
  file: File
  eventType: 'Check-in' | 'Check-out' | 'Check-in e Check-out' | null
  newName: string
  isEditing: boolean
}

const fileInput = ref<HTMLInputElement | null>(null)
const filesToUpload = ref<StagedFile[]>([])

function cancelEditName(item: StagedFile) {
  item.isEditing = false
  item.newName = item.file.name
}

function cancelEditCheckEvidenceName(item: CheckEvidence) {
  item.isEditing = false
  item.newName = item.name
}

function formatBytes(bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

function triggerFileInput() {
  if (blockManualEvidenceUpload()) return

  fileInput.value?.click()
}

function addFilesToList(newFiles: FileList | null) {
  if (blockManualEvidenceUpload()) return
  if (!newFiles) return

  for (const file of Array.from(newFiles)) {
    if (file.size > maxFileSize) {
      alert(
        `O arquivo "${file.name}" excede o tamanho máximo de ${maxFileSize / (1024 * 1024)} MB.`
      )
      continue
    }

    if (!filesToUpload.value.some((f) => f.file.name === file.name && f.file.size === file.size)) {
      filesToUpload.value.push({
        id: Math.random() * 10000,
        file: file,
        eventType: null,
        newName: file.name,
        isEditing: false
      })
    }
  }
}

function onFileSelect(event: Event) {
  const target = event.target as HTMLInputElement
  addFilesToList(target.files)
  if (target) target.value = ''
}

function onDrop(event: DragEvent) {
  if (blockManualEvidenceUpload()) return

  addFilesToList(event.dataTransfer?.files || null)
}

function removeFile(index: number) {
  filesToUpload.value.splice(index, 1)
}

const isFormValid = computed(() => {
  const hasFiles = filesToUpload.value.length > 0

  if (hasFiles) {
    const allFilesValid = filesToUpload.value.every((f) => f.eventType)
    return allFilesValid
  }

  return hasBackendChanges.value
})

function submitFiles() {
  const index = currentCheckIndex.value

  console.log('Submitting files for check index:', index)

  if (index === undefined) return

  if (filesToUpload.value.length > 0) {
    console.log('Files to upload:', filesToUpload.value)
    const newFiles: File[] = filesToUpload.value.map((f) => {
      const originalName = f.file.name
      const lastDotIndex = originalName.lastIndexOf('.')

      if (lastDotIndex !== -1) {
        const extension = originalName.substring(lastDotIndex)
        if (!f.newName.toLowerCase().endsWith(extension.toLowerCase())) {
          f.newName += extension
        }
      }

      if (f.newName === f.file.name) {
        return f.file
      }
      return new File([f.file], f.newName, { type: f.file.type })
    })

    const newCheckEvidences: CheckEvidence[] = filesToUpload.value.map((f) => ({
      name: f.newName,
      path: '',
      eventType: f.eventType
    }))

    draftInformations.value[index].evidence = [
      ...(draftInformations.value[index].evidence || []),
      ...newFiles
    ]
    draftInformations.value[index].checkEvidences = [
      ...(draftInformations.value[index].checkEvidences || []),
      ...newCheckEvidences
    ]

    selectedFileNames.value[index] = [
      ...(selectedFileNames.value[index] || []),
      ...newCheckEvidences
    ]
  }

  closeModal()
}

function isIsoOffset(tz: string | undefined): boolean {
  if (!tz) return false
  if (tz === 'Z' || tz === 'z') return true
  const m = tz.match(/^([+-])(\d{2})(?::?(\d{2}))?$/)
  if (!m) return false
  return true
}

function parseDateSafely(dateString: string | null | undefined): Date | null {
  if (!dateString) return null
  const date = new Date(dateString)
  return isNaN(date.getTime()) ? null : date
}

function formatSafe(date: Date | null, formatStr: string): string {
  if (!date || isNaN(date.getTime())) return ''
  return format(date, formatStr)
}

function processChecks() {
  const tzInput = props.timezone ?? ''
  const timezoneOrDefaultTimezone = isIsoOffset(tzInput) ? tzInput : '-03:00'

  informations.value = (props.infos as CheckRow[]).map((v, i) => {
    if (v.isMain) isCheckMain.value = i

    const rawCheckin = parseDateSafely(v.checkin)
    const checkinDate = rawCheckin ? utcToZonedTime(rawCheckin, timezoneOrDefaultTimezone) : null

    const rawCheckout = parseDateSafely(v.checkout)
    const checkoutDate = rawCheckout ? utcToZonedTime(rawCheckout, timezoneOrDefaultTimezone) : null

    return {
      id: v.id,
      checkin: formatSafe(checkinDate, 'HH:mm'),
      labelCheckin: formatSafe(checkinDate, 'dd/MM/yyyy'),
      checkinLatitude: v.checkinLatitude,
      checkoutLongitude: v.checkoutLongitude,
      checkout: formatSafe(checkoutDate, 'HH:mm'),
      labelCheckout: formatSafe(checkoutDate, 'dd/MM/yyyy'),
      checkoutLatitude: v.checkoutLatitude,
      checkinLongitude: v.checkinLongitude,
      origin: v.origin ?? CheckOrigin.DOCTOR,
      isMain: v.isMain,
      checkEvidences: v.checkEvidences && v.checkEvidences.length > 0 ? v.checkEvidences : [],
      evidence: v.evidence || [],
      permanence: v.permanence ?? null,
      dsAttendanceContainerId: v.dsAttendanceContainerId ?? null
    }
  })
  draftInformations.value = informations.value.map((o) => ({ ...o }))
  selectedFileNames.value = draftInformations.value.map((item) => {
    if (item.checkEvidences && item.checkEvidences.length > 0) {
      return item.checkEvidences
    } else if (item.evidence && item.evidence.length > 0) {
      return item.evidence.map((file: File) => ({
        name: file.name,
        path: '',
        eventType: null
      }))
    }
    return []
  })
}

onMounted(() => {
  processChecks()
})

watch(
  () => props.infos,
  (newInfos) => {
    if (isSavingDraft.value) {
      isSavingDraft.value = false
      return
    }

    if (newInfos) {
      processChecks()
    }
  },
  { deep: true, immediate: true }
)

const colorHex = computed(() => (toggleEdit.value ? '#2F7F91' : '#D3D3D3'))
const colorText = computed(() => (toggleEdit.value ? 'text-primary' : 'text-gray-300'))
const borderColor = computed(() => (toggleEdit.value ? 'border-primary30' : 'border-gray-300'))

// Gate no consumo (não dentro de `PermanenceLinkRow.vue`): o componente é uma
// peça de apresentação pura, reutilizada tal-qual pela Task 7, seguindo o
// mesmo padrão de `LabelBadge`/`withIcon.vue` — quem decide se o vínculo
// aparece é quem consome, não o componente em si.
const permanenceLinks = computed(() =>
  flags.enabledPermanenceRecord ? getPermanenceLinkRows(props.infos) : []
)

function addNewCheck() {
  // Guardas de defesa: com o bloqueio ligado a UI nem oferece o caminho, mas o
  // método continua exportado para o modo legado — e a API recusa de todo jeito
  // (`MANUAL_CHECK_ENTRY_BLOCKED`).
  if (isReadOnly.value) return

  draftInformations.value.push({
    checkin: '',
    checkout: '',
    origin: CheckOrigin.WEB,
    isMain: false,
    evidence: []
  })

  selectedFileNames.value.push([])
}

function removeCheck(pos: number) {
  if (isReadOnly.value) return

  draftInformations.value = draftInformations.value.filter((_v, i) => i !== pos)

  selectedFileNames.value.splice(pos, 1)
}

function closeEditMode() {
  toggleEdit.value = false
  draftInformations.value = informations.value.map((o) => ({ ...o }))
}

function saveEditMode() {
  if (isReadOnly.value) return

  let validForm = true

  // `isMain` não é mais decidido aqui (BF5): o servidor reelege o principal em
  // `ensureMainCheck` e ignora o campo do payload (`StationService` já para de
  // enviá-lo). O valor local segue intacto só para o indicador de leitura.
  const newDrafts = draftInformations.value.map((c) => {
    if (!validateTime(c.checkin)) validForm = false
    return { ...c }
  })

  if (!validForm) return

  toggleEdit.value = false
  isSavingDraft.value = true
  emit('saveChecks', newDrafts)
  informations.value = newDrafts.map((o) => ({ ...o }))
}

function validateTime(hours: string) {
  const [hour, minutes] = hours.split(':')
  const isValidHours = Number(hour) >= 0 && Number(hour) <= 23
  const isValidMinutes = Number(minutes) >= 0 && Number(minutes) <= 59
  return isValidHours && isValidMinutes
}

const showModal = ref(false)
const flowDisabledOpen = ref(false)

/**
 * F2/Q32 — o bloqueio da inserção manual passa a ser de verdade, na API, e chega
 * ao web por `registration-settings` (`manualCheckBlockEnabled` → `isReadOnly`).
 *
 * O kill switch de front continua aqui como SEGUNDA TRAVA, e não por descuido:
 * ele está ligado em PROD desde 10/09 e a rota `registration-settings` ainda não
 * existe — enquanto ela responder 404, o campo cai no default `false` e só esta
 * flag segura o upload manual. Ordem de precedência:
 *
 * 1. `manualCheckBlockEnabled === true` → a aba inteira é somente leitura e nem
 *    existe caminho de upload (este guard nunca é alcançado);
 * 2. campo ausente/`false` → vale o comportamento de hoje, incluindo o modal
 *    "Procure sua liderança" quando a env var estiver ligada.
 *
 * A flag sai quando o backend da F2 estiver em PROD com `DS_MANUAL_CHECK_BLOCK_ENABLED`
 * ligado (§4.1/§10.4) — nunca antes.
 */
function blockManualEvidenceUpload(): boolean {
  if (!flags.disabledManualEvidenceUpload) return false

  flowDisabledOpen.value = true
  return true
}

const currentCheckIndex = ref<number>(0)
const showFilesModal = ref(false)
const modalFiles = ref<CheckEvidence[]>([])

function showFiles(fileNames: CheckEvidence[], index: number) {
  hasBackendChanges.value = false

  fileNames.forEach((f) => {
    if (f.isEditing === undefined) f.isEditing = false
    f.newName = f.name
  })

  modalFiles.value = fileNames
  currentCheckIndex.value = index

  console.log(
    'showFiles called with index:',
    index,
    'fileNames:',
    fileNames,
    'toggleEdit:',
    toggleEdit.value
  )
  if (toggleEdit.value) {
    showModal.value = true
  } else {
    showFilesModal.value = true
  }
}

function saveCheckEvidenceName(item: CheckEvidence & { isEditing?: boolean; newName?: string }) {
  console.log('Saving new name:', item.newName)
  if (item.newName && item.newName.trim() !== '') {
    const originalName = item.name
    const lastDotIndex = originalName.lastIndexOf('.')

    if (lastDotIndex !== -1) {
      const extension = originalName.substring(lastDotIndex)

      if (!item.newName.toLowerCase().endsWith(extension.toLowerCase())) {
        item.newName += extension
      }
    }
    console.log('Saving new name:', item.newName, 'original name:', originalName)
    if (item.name !== item.newName) {
      console.log('Name changed from', item.name, 'to', item.newName)
      item.name = item.newName
      hasBackendChanges.value = true
    }

    item.isEditing = false
  } else {
    item.newName = item.name
    item.isEditing = false
  }
}

function closeFilesModal() {
  showFilesModal.value = false
  showModal.value = false
}

function openModal(index: number) {
  if (isReadOnly.value) return
  // Bloqueio temporário do upload de evidência pelo painel (set/2026): com a env
  // var ligada o diálogo não abre e o modal de aviso aparece no lugar.
  if (blockManualEvidenceUpload()) return

  hasBackendChanges.value = false
  modalFiles.value = []
  currentCheckIndex.value = index

  if (toggleEdit.value) {
    showModal.value = true
  } else {
    showFilesModal.value = true
  }
}

// function showFiles(fileNames: CheckEvidence[], index: number) {

//   hasBackendChanges.value = false

//   fileNames.forEach(f => {
//     if (f.isEditing === undefined) f.isEditing = false
//     f.newName = f.name
//   })

//   modalFiles.value = fileNames
//   currentCheckIndex.value = index
//   showFilesModal.value = true
// }

function closeModal() {
  showModal.value = false
  showFilesModal.value = false
  filesToUpload.value = []
}

/**
 * Baixa a evidência de check via endpoint autenticado e abre o blob local
 * em nova aba. Evita expor a URL crua do Azure Blob (que agora é um token
 * cifrado opaco em `item.path`).
 */
async function downloadEvidence(evidenceId: number | undefined) {
  if (!evidenceId) return
  try {
    const blob = await NetworkService.doctorCheckEvidence.downloadCheckEvidenceFile(
      props.stationId,
      evidenceId
    )
    const blobUrl = URL.createObjectURL(blob)
    window.open(blobUrl, '_blank')
  } catch (err) {
    console.error('Falha ao baixar evidência de check:', err)
    // Antes: swallow com console.error — o clique em baixar falhava em silêncio.
    notifyError(err, 'Não foi possível baixar a evidência de registro')
  }
}

const maxFileSize = 5 * 1024 * 1024

// function fileSizeRule(files: File[] | null): true | string {
//   if (!files || files.length === 0) return true
//   for (const file of files) {
//     if (file.size > maxFileSize) {
//       return `O tamanho máximo permitido é ${maxFileSize / (1024 * 1024)} MB.`
//     }
//   }
//   return true
// }

function removeBackendFile(index: number) {
  const checkIdx = currentCheckIndex.value

  if (checkIdx === undefined) return

  hasBackendChanges.value = true

  modalFiles.value.splice(index, 1)

  if (draftInformations.value[checkIdx]) {
    draftInformations.value[checkIdx].checkEvidences = [...modalFiles.value]

    selectedFileNames.value[checkIdx] = [...modalFiles.value]
  }
}

function downloadLocalFile(file: File) {
  if (!file) return

  const url = URL.createObjectURL(file)

  const link = document.createElement('a')
  link.href = url
  link.download = file.name

  document.body.appendChild(link)

  link.click()

  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
</script>

<style scoped>
:deep(.v-field__input) {
  width: 6.4em;
  text-align: center;
}
:deep(.v-input--disabled .v-input__prepend) {
  opacity: 1;
}

.custom-cursor:hover {
  cursor: crosshair;
}

.custom-dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  border: 3px dashed rgb(var(--v-theme-primary));
  border-radius: 8px;
  min-height: 200px;
  padding: 40px;

  width: 100%;
  transition: background-color 0.2s ease-in-out;
}

.custom-dropzone:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.select-category :deep(.v-field) {
  border: 1px solid black !important;
}

.select-category :deep(.v-field--focused) {
  border-color: black !important;
}

.file-list-item {
  margin-top: 16px;
  margin-bottom: 16px;

  display: flex;
  align-items: center;
}

.file-list-item-row {
  display: flex;
  align-items: center;
  gap: 16px;

  align-items: center;
  flex-wrap: nowrap;
}

.file-list-item-col {
  gap: 16px;
  align-items: center;
}

.col-texto-flexivel {
  flex: 1 1 auto;
  min-width: 0;
}

.col-texto-flexivel .font-weight-bold {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.col-texto-flexivel .v-list-item-title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  font-weight: bold;
}
</style>
